import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="kategorien" className="relative py-16 md:py-24 bg-background-100">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'url("https://readdy.ai/api/search-image?query=Seamless%20repeating%20pattern%20of%20delicate%20hand-drawn%20fashion%20product%20line%20icons%20shoes%20bags%20caps%20shirts%20in%20soft%20pencil%20sketch%20style%20on%20transparent%20background%2C%20varied%20sizes%20and%20orientations%2C%20artistic%20minimal%20pattern&width=800&height=800&seq=bg-pattern-products-v2&orientation=squarish")',
          backgroundSize: '400px 400px',
        }}
      ></div>

      <div className="relative w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 md:mb-14 gap-6">
          <div>
            <p className="text-xs md:text-sm font-medium tracking-widest uppercase text-primary-600 mb-3">
              Produkt Kategorien
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              Finde deinen Style
            </h2>
            <p className="font-heading text-xl md:text-2xl italic text-foreground-400 mt-1">
              für jeden Geschmack
            </p>
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center justify-between gap-3 px-5 py-3 min-w-[240px] text-sm font-medium rounded-xl bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-primary-500"></i>
                {selectedCategory || 'Kategorie wählen'}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-[320px] max-h-[480px] overflow-y-auto bg-background-50 border border-background-200 rounded-xl z-40 py-2">
                {categories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleCategorySelect(cat)}
                    className="w-full flex items-center gap-4 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 transition-colors text-left cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.thumb} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="whitespace-nowrap flex-1 font-medium">{cat.name}</span>
                    <i className="ri-external-link-line text-foreground-300 text-xs"></i>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat, idx) => (
            <a
              key={idx}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group relative rounded-xl overflow-hidden border border-background-200/50 hover:border-primary-300 transition-all duration-300 cursor-pointer bg-background-50"
            >
              <div className="aspect-[4/5] relative overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${cat.name} Kategorie`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-3 md:p-4">
                <p className="text-sm md:text-base font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {cat.name}
                </p>
                <p className="text-xs text-foreground-400 mt-0.5">{cat.nameDe}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}